<script setup>
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';
import { computed, watch, ref, onMounted } from 'vue';
import { BookOpen } from 'lucide-vue-next';
import { cn } from '@/lib/utils';
import { resolveSeedeStyle } from '@/lib/gen/prompt-parser';
import { request } from '@/lib/api';

const props = defineProps({
  scene: Object,
  class: {
    type: String,
    default: '',
  },
  open: Boolean,
});

const emit = defineEmits(['update:open']);

const isOpen = computed({
  get: () => props.open,
  set: (v) => emit('update:open', v),
});

const modelValue = ref('');

const hoveredStyle = ref(null);
const loadedStyles = ref([]);
const loading = ref(false);
const customStyle = ref(null);

defineExpose({
  getStyle,
  resetStyle,
  setStyle,
});

watch(
  () => props.scene,
  async (newScene) => {
    if (newScene?.tag) {
      await loadStyles(newScene.tag);
    }
  },
);

onMounted(async () => {
  if (props.scene?.tag) {
    await loadStyles(props.scene.tag);
  }
});

async function loadStyles(tag) {
  loading.value = true;
  try {
    const data = await request(`/prompt/list?category=${tag}`);
    loadedStyles.value = data.data || [];
  } catch (error) {
    console.error('Failed to load styles:', error);
    loadedStyles.value = [];
  } finally {
    loading.value = false;
  }
}

function setStyle(style) {
  if (!style) return;

  // Check if style exists in loaded styles
  const existingStyle = loadedStyles.value.find((s) => s.value === style.value);

  if (existingStyle) {
    // Style exists in list, just set the value
    modelValue.value = style.value;
    customStyle.value = null;
  } else {
    // Style not in list, store it as custom style
    customStyle.value = style;
    modelValue.value = style.value;
  }
}

function getStyle() {
  const list = posterStyles.value;
  const item = list.find((item) => item.value === modelValue.value);
  if (!item) return '';

  return `
${resolveSeedeStyle(item.value, item.label)}
`;
}

function resetStyle() {
  modelValue.value = '';
  customStyle.value = null;
}

function handleStyleMouseEnter(event, style) {
  hoveredStyle.value = style;
  if (modelValue.value !== style.value) {
    event.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
  }
}

function handleStyleMouseLeave(event, style) {
  if (modelValue.value !== style.value) {
    event.currentTarget.style.backgroundColor = '';
  }
}

const posterStyles = computed(() => {
  // Merge loaded styles with custom style if it exists and not in list
  const styles = [...loadedStyles.value];

  if (customStyle.value) {
    const exists = styles.some((s) => s.value === customStyle.value.value);
    if (!exists) {
      styles.push(customStyle.value);
    }
  }

  return styles;
});

const selectedValue = computed(() => {
  const list = posterStyles.value;
  if (!modelValue.value || list.length === 0) return { value: 'default', label: 'Default' };
  return list.find((style) => style.value === modelValue.value) || list[0];
});
</script>
<template>
  <div class="flex w-full space-x-2">
    <Select v-model="modelValue" v-model:open="isOpen">
      <SelectTrigger
        :class="
          cn(
            'group transition-shadow duration-300 gap-1 pr-1 hover:bg-black/5 hover:border-black/20 data-[state=open]:bg-black/5 data-[state=open]:border-black/20',
            props.class,
          )
        "
      >
        <BookOpen class="size-4 block text-black/60" />
        <div class="hidden md:flex gap-0 items-center flex-1">
          <span
            class="text-xs truncate max-w-[120px] text-black/60 group-hover:text-black/90 transition-colors"
          >
            {{ $t(`posterStyles.${selectedValue?.value}`, selectedValue?.label) }}
          </span>
        </div>
      </SelectTrigger>
      <SelectContent
        class="min-w-[520px] p-0 flex rounded-[16px] overflow-hidden backdrop-blur-[40px]"
        style="
          background-color: rgba(255, 255, 255, 0.8);
          border: 1px solid rgba(0, 0, 0, 0.07);
          box-shadow:
            0 20px 25px -5px rgba(0, 0, 0, 0.1),
            0 8px 10px -6px rgba(0, 0, 0, 0.1);
        "
      >
        <div class="flex h-[300px]">
          <div class="w-[240px] h-full border-r border-black/5 overflow-y-auto custom-scrollbar">
            <SelectGroup>
              <SelectItem
                v-for="style in posterStyles"
                :key="style.id"
                :value="style.value"
                class="w-full relative transition-colors duration-200 px-4 py-3 rounded-lg mb-0.5"
                :style="modelValue === style.value ? 'background-color: rgba(0, 0, 0, 0.05)' : ''"
                @mouseenter="handleStyleMouseEnter($event, style)"
                @mouseleave="handleStyleMouseLeave($event, style)"
                @focus="hoveredStyle = style"
              >
                <div class="flex flex-col">
                  <span
                    class="text-sm font-normal"
                    :class="modelValue === style.value ? 'text-black' : 'text-black/50'"
                  >
                    {{ $t(`posterStyles.${style.value}`, style.label) }}
                  </span>
                  <span
                    v-if="style.meta?.description"
                    class="text-xs font-normal mt-0.5 line-clamp-1"
                    :class="modelValue === style.value ? 'text-black' : 'text-black/50'"
                  >
                    {{ style.meta.description }}
                  </span>
                </div>
              </SelectItem>
            </SelectGroup>
          </div>
          <div class="w-[280px] overflow-hidden bg-black/5">
            <div v-if="hoveredStyle" class="relative h-full">
              <img
                :src="hoveredStyle.preview_image"
                :alt="hoveredStyle.label"
                class="w-full h-[280px] object-contain transform transition-transform duration-300 hover:scale-105"
              />
              <div
                class="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent h-20 flex items-end p-4"
              >
                <span class="text-white text-sm font-medium block">
                  {{ $t(`posterStyles.${hoveredStyle.value}`, hoveredStyle.label) }}
                </span>
              </div>
            </div>
            <div
              v-else
              class="flex items-center justify-center h-full text-black/40 text-sm bg-gradient-to-br from-white to-black/5"
            >
              <span class="animate-pulse">Hover over a style to preview</span>
            </div>
          </div>
        </div>
      </SelectContent>
    </Select>
  </div>
</template>

<style>
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
